import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import FileSaver from 'file-saver';
import { ScaleLoader } from 'react-spinners';
import { AppContext, ApplicationContext } from '../../context/Contexts';
import { CaseAttachment } from '../../interfaces/Case';

export interface AppointmentDamagePhotosProps {
  damagePhotos: CaseAttachment[];
  appointmentId: number;
}

interface AttachmentWithUrl extends CaseAttachment {
  url?: string;
}

const AppointmentDamagePhotos = ({ damagePhotos, appointmentId }: AppointmentDamagePhotosProps) => {
  const context = useContext(ApplicationContext) as AppContext;
  const { caseService, translatorService } = context;

  const [attachmentsWithUrl, setAttachmentsWithUrl] = useState<AttachmentWithUrl[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!damagePhotos?.length) {
      setAttachmentsWithUrl([]);
      setIsLoading(false);
      urlsRef.current = [];
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    const loadUrls = async () => {
      const processed = await Promise.all(
        damagePhotos.map(async (file) => {
          try {
            const fileBlob = await caseService.GetCaseAttachment(file.fileName);
            const url = URL.createObjectURL(fileBlob);
            return { ...file, url };
          } catch {
            return { ...file };
          }
        })
      );
      if (!cancelled) {
        urlsRef.current = processed.map((a) => a.url).filter(Boolean) as string[];
        setAttachmentsWithUrl(processed);
      } else {
        processed.forEach((a) => a.url && URL.revokeObjectURL(a.url));
      }
    };
    loadUrls().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damagePhotos?.length, damagePhotos?.map((a) => a.fileName).join(',')]);

  const downloadFile = useCallback(
    async (fileName: string, origFileName: string) => {
      const fileBlob = await caseService.GetCaseAttachment(fileName);
      FileSaver.saveAs(fileBlob, origFileName);
    },
    [caseService]
  );

  const openImagePreview = (index: number) => {
    setCurrentIndex(index);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const renderPreviewContent = () => {
    const attach = attachmentsWithUrl[currentIndex];
    if (!attach) return null;
    const src = attach.url || '';
    return (
      <img
        alt="Preview"
        src={src}
        style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
      />
    );
  };

  const renderAttachments = () => (
    <Box display="flex" style={{ gap: 16, flexWrap: 'wrap', width: '100%' }}>
      {attachmentsWithUrl.map((photo: AttachmentWithUrl, index: number) => {
        const src = photo?.url || '';
        return (
          <div
            key={photo?.originalFileName ?? index}
            className="border"
            style={{
              margin: 10,
              width: 250,
              height: 200,
              position: 'relative',
              flexShrink: 0,
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                right: 0,
                top: 0
              }}
            >
              <IconButton
                aria-label="preview"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openImagePreview(index);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              {photo.id !== 0 ? (
                <IconButton
                  aria-label="download"
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(photo.fileName, photo.originalFileName ?? '');
                  }}
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              ) : null}
            </div>
            <div
              className="card-body text-center d-flex justify-content-center align-items-center"
              style={{ height: '100%', width: '100%', cursor: 'pointer', padding: 8 }}
              onClick={() => openImagePreview(index)}
            >
              <img
                alt="Damage car"
                src={src}
                style={{
                  width: 200,
                  height: 150,
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        );
      })}
    </Box>
  );

  return (
    <Fragment>
      <div className="d-flex flex-row text-center flex-wrap justify-content-center">
        <ScaleLoader color={'var(--primary)'} loading={isLoading} />
      </div>
      {!isLoading ? <Box style={{ marginLeft: 0 }}>{renderAttachments()}</Box> : null}
      <Dialog
        onClose={closeDialog}
        aria-labelledby="customized-dialog-title"
        open={isDialogOpen}
        maxWidth="lg"
        fullWidth
        fullScreen={false}
        classes={{ paper: 'dialogPaper' }}
        scroll="body"
      >
        <DialogTitle id="customized-dialog-title">
          <Grid container>
            <Grid item xs={11}>
              <Typography variant="h4">
                {translatorService.Tranlate('CASE_PREVIEW_DOC', 'Previzualizare document')}
              </Typography>
            </Grid>
            <Grid className="text-right" item xs={1}>
              <IconButton aria-label="close" onClick={closeDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container alignContent="center">
            <Grid item xs={12}>
              <Box height="65vh" className="text-center">
                {renderPreviewContent()}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default AppointmentDamagePhotos;
